import React from 'react';
import Timer from '../../components/Timer';
import { getOneMinute } from '../../constants/tests.js';
import { chunkArray } from '../../utils/arrayUtils';
import { isTablet } from 'react-device-detect';

import AdminTestPanel from '../../components/AdminTestPanel';
import strings from '../../strings';
import AdminReadyButton from '../../components/AdminReadyButton';

const ITEMS_PER_COLUMN = isTablet ? 7 : 10;
const TIMER_DURATION = 60;
const TEST_STAGE = 1;
const ANSWER_LOCK_MS = 150;

class Admin extends AdminTestPanel {
	hardTimeout = null;
answerUnlockTimeout = null;
deadline = null;

hasFinished = false;
answerLocked = false;
waitingAdvanced = false;

	constructor(props) {
		super(props, {
			enableTimers: false,
		});
	}

	componentDidMount() {
	if (super.componentDidMount) {
		super.componentDidMount();
	}

	// If the component mounts while already on the test stage,
	// start the 60-second hard stop immediately.
	if (this.state.currentStage === 1) {
		this.resetRunFlags();
		this.startHardTimeout();
	}
}

	componentDidUpdate(prevProps, prevState) {
	// entered test stage
	if (
		prevState.currentStage !== this.state.currentStage &&
		this.state.currentStage === 1
	) {
		this.resetRunFlags();
		this.startHardTimeout();
	}

	// step actually moved -> release rapid-press lock
	if (prevState.currentStep !== this.state.currentStep) {
		this.releaseAnswerLock();
	}

	// left test stage -> always stop timers and block any late submit
	if (
		prevState.currentStage === 1 &&
		this.state.currentStage !== 1
	) {
		this.hasFinished = true;
		this.clearHardTimeout();
		this.releaseAnswerLock();
	}
}

	componentWillUnmount() {
	this.clearHardTimeout();
	this.clearAnswerUnlockTimeout();

	if (super.componentWillUnmount) {
		super.componentWillUnmount();
	}
}

	resetRunFlags = () => {
	this.hasFinished = false;
	this.answerLocked = false;
	this.waitingAdvanced = false;
	this.clearAnswerUnlockTimeout();
};

	clearHardTimeout = () => {
	if (this.hardTimeout) {
		clearTimeout(this.hardTimeout);
		this.hardTimeout = null;
	}
	this.deadline = null;
};

	startHardTimeout = () => {
	this.clearHardTimeout();

	this.deadline = Date.now() + TIMER_DURATION * 1000;

	this.hardTimeout = setTimeout(() => {
		this.finishTestOnce(this.state.results);
	}, TIMER_DURATION * 1000);
};

	clearAnswerUnlockTimeout = () => {
	if (this.answerUnlockTimeout) {
		clearTimeout(this.answerUnlockTimeout);
		this.answerUnlockTimeout = null;
	}
};


	lockAnswer = () => {
	this.answerLocked = true;
	this.clearAnswerUnlockTimeout();

	// Fallback unlock so fast presses do not freeze the test
	this.answerUnlockTimeout = setTimeout(() => {
		if (!this.hasFinished && this.state.currentStage === 1) {
			this.answerLocked = false;
		}
	}, 150);
};

	releaseAnswerLock = () => {
	this.clearAnswerUnlockTimeout();
	this.answerLocked = false;
};

isLastItem = () => {
	const steps = getOneMinute().getSteps();
	return this.state.currentStep >= steps.length - 1;
};

prepareFinalAnswer = () => {
	// Stop any later timeout from submitting again
	this.hasFinished = true;
	this.answerLocked = true;
	this.clearAnswerUnlockTimeout();
	this.clearHardTimeout();
};

	finishTestOnce = (results = this.state.results) => {
	if (this.hasFinished) return;

	this.hasFinished = true;
	this.answerLocked = true;
	this.clearAnswerUnlockTimeout();
	this.clearHardTimeout();

	this.props.nextStage(results);
};

	onTimerEnd = () => {
		this.finishTestOnce(this.state.results);
	};

	canAcceptAnswer = () => {
	if (this.hasFinished) return false;
	if (this.answerLocked) return false;
	if (this.state.currentStage !== 1) return false;

	if (this.deadline && Date.now() >= this.deadline) {
		this.finishTestOnce(this.state.results);
		return false;
	}

	return true;
};

	// Override actual answer methods so BOTH buttons and hotkeys
	// go through the same guard.
	answerCorrect() {
	if (!this.canAcceptAnswer()) return;

	if (this.isLastItem()) {
		this.prepareFinalAnswer();
	} else {
		this.lockAnswer();
	}

	return super.answerCorrect();
}

answerWrong() {
	if (!this.canAcceptAnswer()) return;

	if (this.isLastItem()) {
		this.prepareFinalAnswer();
	} else {
		this.lockAnswer();
	}

	return super.answerWrong();
}

answerUnanswered() {
	if (!this.canAcceptAnswer()) return;

	if (this.isLastItem()) {
		this.prepareFinalAnswer();
	} else {
		this.lockAnswer();
	}

	return super.answerUnanswered();
}

	handleAnswerCorrect = (e) => {
	e.stopPropagation();
	this.answerCorrect();
};

handleAnswerWrong = (e) => {
	e.stopPropagation();
	this.answerWrong();
};

handleAnswerUnanswered = (e) => {
	e.stopPropagation();
	this.answerUnanswered();
};

	handleWaitingNext = () => {
	if (this.waitingAdvanced) return;
	this.waitingAdvanced = true;
	this.props.nextStage();
};

	getTestName = () => {
		return strings.om_testname;
	};

	getClass = (row, index) => {
		const actualIndex = row * ITEMS_PER_COLUMN + index;
		const entry = this.state.results[actualIndex];

		if (!entry) return '';
		if (!entry.isAnswered) return ' unanswered';
		if (entry.isCorrect) return ' correct';

		return ' error';
	};

	cycleAnswerStatus = (row, index) => {
		const actualIndex = row * ITEMS_PER_COLUMN + index;
		const itemsCopy = JSON.parse(JSON.stringify(this.state.results));
		const entry = itemsCopy[actualIndex];

		let currentStatus;
		if (!entry) currentStatus = 'unanswered';
		else if (!entry.isAnswered) currentStatus = 'unanswered';
		else if (entry.isCorrect) currentStatus = 'correct';
		else currentStatus = 'wrong';

		let newState = {};
		if (currentStatus === 'unanswered')
			newState = { isAnswered: true, isCorrect: true };
		else if (currentStatus === 'correct')
			newState = { isAnswered: true, isCorrect: false };
		else if (currentStatus === 'wrong')
			newState = { isAnswered: false, isCorrect: false };

		itemsCopy[actualIndex] = Object.assign(
			{},
			itemsCopy[actualIndex],
			newState
		);

		this.setState({
			results: itemsCopy,
		});
	};

	renderIntro = () => {
		return (
			<div>
				<p>{strings.om_admin1}</p>
				<p>{strings.om_admin2}</p>
				<p>{strings.om_admin3}</p>
				<p>{strings.hotkeyCorrect}</p>
				<p>{strings.hotkeyWrong}</p>
				<p>{strings.hotUnanswered2}</p>
				<p>{strings.clickStartWhenReady2}</p>
				<AdminReadyButton
					remoteIsReady={this.props.candidateReady}
					onClick={this.props.nextStage}
				/>
			</div>
		);
	};

	renderTest = () => {
		const { currentStep } = this.state;
		const steps = getOneMinute().getSteps();

		const rows = chunkArray(steps, ITEMS_PER_COLUMN);
		const currentRow = Math.floor(currentStep / ITEMS_PER_COLUMN);
		const currentItem = Math.floor(currentStep % ITEMS_PER_COLUMN);

		const row = rows[currentRow] || null;
		const previousRow = rows[currentRow - 1] || null;
		const nextRow = rows[currentRow + 1] || null;

		return (
			<div className="oneminute-admin">
				<p>{strings.om_admin1}</p>
				<div className="admin-inline">
					<p>
						{strings.formatString(
							strings.taskNumOf,
							currentStep + 1,
							steps.length
						)}{' '}
					</p>
					<Timer
						duration={TIMER_DURATION}
						onEnd={this.onTimerEnd}
						autoStart
					/>
				</div>

				<div className="TestWrapper">
					<div className="column previous">
						{previousRow &&
							previousRow.map((item, index) => (
								<p
									key={index}
									className={`item ${this.getClass(
										currentRow - 1,
										index
									)}`}
									onClick={() =>
										this.cycleAnswerStatus(
											currentRow - 1,
											index
										)
									}
								>
									{item}
								</p>
							))}
					</div>

					<div className="column">
						{row &&
							row.map((item, index) => (
								<p
									key={index}
									className={`item${
										currentItem === index ? ' active' : ''
									}${this.getClass(currentRow, index)}`}
									onClick={() =>
										this.cycleAnswerStatus(
											currentRow,
											index
										)
									}
								>
									{item}
								</p>
							))}
					</div>

					<div className="column next">
						{nextRow &&
							nextRow.map((item, index) => (
								<p
									key={index}
									className={`item ${this.getClass(
										currentRow + 1,
										index
									)}`}
									onClick={() =>
										this.cycleAnswerStatus(
											currentRow + 1,
											index
										)
									}
								>
									{item}
								</p>
							))}
					</div>
				</div>

				<button onClick={this.handleAnswerCorrect}>
					{strings.hotkeyButtonCorrect}
				</button>
				<button onClick={this.handleAnswerWrong}>
					{strings.hotkeyButtonWrong}
				</button>
				<button onClick={this.handleAnswerUnanswered}>
					{strings.hotkeyButtonUnanswered}
				</button>
			</div>
		);
	};

	renderWaiting = () => {
		return (
			<div>
				<p>
					{strings.formatString(
						strings.testDoneAndFinished,
						strings.om_testname
					)}
				</p>
				<button onClick={this.handleWaitingNext}>
					{'Nästa'}
				</button>
			</div>
		);
	};
}

export default Admin;